import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  BookOpenIcon,
  ClipboardListIcon,
  EditIcon,
  TrashIcon,
  CalendarIcon,
  SearchIcon,
  FilterIcon,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Navbar, ConfirmationDialog } from "../../components/common";
import {
  getLessonPlans,
  getAssessments,
  deleteLessonPlan,
  deleteAssessment,
  LessonPlan,
  Assessment,
} from "../../lib/supabase";
import { getCurrentUser } from "../../lib/authUtils";
import toast from "react-hot-toast";

type HistoryItem = (LessonPlan | Assessment) & {
  type: "lesson_plan" | "assessment";
};
type FilterType = "all" | "lesson_plan" | "assessment";

export const HistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState<FilterType>("all");

  // Confirmation dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<HistoryItem | null>(null);

  useEffect(() => {
    loadHistoryData();
  }, []);

  useEffect(() => {
    filterAndSearchItems();
  }, [historyItems, searchQuery, filterType]);

  const loadHistoryData = async () => {
    setLoading(true);
    try {
      const { user, error: authError } = await getCurrentUser();
      if (authError || !user) {
        toast.error("Sesi tidak valid. Silakan login kembali.");
        return;
      }

      const [lessonPlansResult, assessmentsResult] = await Promise.all([
        getLessonPlans(user.id),
        getAssessments(user.id),
      ]);

      const items: HistoryItem[] = [];

      if (lessonPlansResult.success && lessonPlansResult.data) {
        items.push(
          ...lessonPlansResult.data.map(item => ({
            ...item,
            type: "lesson_plan" as const,
          }))
        );
      }

      if (assessmentsResult.success && assessmentsResult.data) {
        items.push(
          ...assessmentsResult.data.map(item => ({
            ...item,
            type: "assessment" as const,
          }))
        );
      }

      // Sort by created_at descending
      items.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      setHistoryItems(items);
    } catch {
      toast.error("Gagal memuat data riwayat");
    } finally {
      setLoading(false);
    }
  };

  const filterAndSearchItems = () => {
    let filtered = historyItems;

    // Apply type filter
    if (filterType !== "all") {
      filtered = filtered.filter(item => item.type === filterType);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        item =>
          item.title.toLowerCase().includes(query) ||
          item.mata_pelajaran.toLowerCase().includes(query) ||
          item.topik_tujuan.toLowerCase().includes(query)
      );
    }

    setFilteredItems(filtered);
  };

  const handleDeleteItem = (item: HistoryItem) => {
    setItemToDelete(item);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      const { user, error: authError } = await getCurrentUser();
      if (authError || !user) {
        toast.error("Sesi tidak valid. Silakan login kembali.");
        setShowDeleteDialog(false);
        setItemToDelete(null);
        return;
      }

      let result;
      if (itemToDelete.type === "lesson_plan") {
        result = await deleteLessonPlan(user.id, itemToDelete.id);
      } else {
        result = await deleteAssessment(user.id, itemToDelete.id);
      }

      if (result.success) {
        toast.success("Item berhasil dihapus");
        // Remove item from local state
        setHistoryItems(prev =>
          prev.filter(historyItem => historyItem.id !== itemToDelete.id)
        );
      } else {
        toast.error(result.error || "Gagal menghapus item");
      }
    } catch {
      toast.error("Terjadi kesalahan saat menghapus item");
    } finally {
      setShowDeleteDialog(false);
      setItemToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setItemToDelete(null);
  };

  const handleEditItem = (item: HistoryItem) => {
    if (item.type === "lesson_plan") {
      navigate("/lesson-plan-builder", { state: { lesson_plan: item } });
    } else {
      navigate("/assessment-builder", { state: { assessment: item } });
    }
  };

  const handleBackToDashboard = () => {
    navigate("/dashboard");
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("id-ID", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getItemIcon = (type: "lesson_plan" | "assessment") => {
    return type === "lesson_plan" ? BookOpenIcon : ClipboardListIcon;
  };

  const getItemTypeLabel = (type: "lesson_plan" | "assessment") => {
    return type === "lesson_plan" ? "Lesson Plan" : "Assessment";
  };

  const getItemTypeBadgeColor = (type: "lesson_plan" | "assessment") => {
    return type === "lesson_plan"
      ? "bg-blue-100 text-blue-800"
      : "bg-green-100 text-green-800";
  };

  const getDeleteDialogMessage = () => {
    if (!itemToDelete) return "";

    const itemType =
      itemToDelete.type === "lesson_plan"
        ? "rencana pembelajaran"
        : "soal assessment";
    return `Apakah Anda yakin ingin menghapus ${itemType} "${itemToDelete.title}"? Tindakan ini tidak dapat dibatalkan.`;
  };

  return (
    <div className="bg-[#eeeff6] w-full min-h-screen flex flex-col">
      {/* Navbar */}
      <Navbar />

      {/* Main Content */}
      <main className="pt-[70px] px-4 sm:px-6 pb-6 flex-1">
        <div className="w-full max-w-[1392px] mx-auto mt-4 sm:mt-8 bg-white rounded-[21px] border-b border-[#f0eff0] p-4 sm:p-8 min-h-[700px]">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6 sm:mb-8">
            <Button
              onClick={handleBackToDashboard}
              variant="ghost"
              className="flex items-center gap-2 text-[#373e46] hover:bg-gray-100 p-2 rounded-lg w-auto"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </Button>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#373e46]">
              Riwayat
            </h2>
          </div>

          {/* Search and Filter */}
          <div className="space-y-4 mb-6">
            {/* Search */}
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Cari berdasarkan judul, mata pelajaran, atau topik..."
                className="w-full pl-10 h-12 rounded-xl border-2 border-[#e3e6eb] focus:border-[#6b2cfe] transition-colors"
              />
            </div>

            {/* Filter */}
            <div className="flex gap-2 overflow-x-auto pb-2">
              <Button
                onClick={() => setFilterType("all")}
                variant={filterType === "all" ? "default" : "outline"}
                className={`px-4 py-2 rounded-xl font-medium transition-all duration-300 whitespace-nowrap flex-shrink-0 ${
                  filterType === "all"
                    ? "bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] text-white"
                    : "border-gray-300 text-[#373e46] hover:bg-gray-50"
                }`}
              >
                Semua
              </Button>
              <Button
                onClick={() => setFilterType("lesson_plan")}
                variant={filterType === "lesson_plan" ? "default" : "outline"}
                className={`px-4 py-2 rounded-xl font-medium transition-all duration-300 flex items-center gap-2 whitespace-nowrap flex-shrink-0 ${
                  filterType === "lesson_plan"
                    ? "bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] text-white"
                    : "border-gray-300 text-[#373e46] hover:bg-gray-50"
                }`}
              >
                <BookOpenIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Lesson Plan</span>
                <span className="sm:hidden">LP</span>
              </Button>
              <Button
                onClick={() => setFilterType("assessment")}
                variant={filterType === "assessment" ? "default" : "outline"}
                className={`px-4 py-2 rounded-xl font-medium transition-all duration-300 flex items-center gap-2 whitespace-nowrap flex-shrink-0 ${
                  filterType === "assessment"
                    ? "bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] text-white"
                    : "border-gray-300 text-[#373e46] hover:bg-gray-50"
                }`}
              >
                <ClipboardListIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Assessment</span>
                <span className="sm:hidden">AS</span>
              </Button>
            </div>
          </div>

          {/* Content */}
          {loading ? (
            /* Loading State */
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div
                  key={i}
                  className="bg-gray-50 rounded-xl p-6 animate-pulse"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="h-6 bg-gray-200 rounded mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                      <div className="flex gap-2">
                        <div className="h-6 bg-gray-200 rounded w-20"></div>
                        <div className="h-6 bg-gray-200 rounded w-16"></div>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
                      <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredItems.length > 0 ? (
            /* History Items */
            <div className="space-y-4">
              {filteredItems.map(item => {
                const Icon = getItemIcon(item.type);
                return (
                  <div
                    key={item.id}
                    className="bg-gray-50 rounded-xl p-4 sm:p-6 border border-gray-200 hover:shadow-md transition-all duration-300 relative"
                  >
                    <div className="flex items-start gap-4">
                      {/* Icon - Smaller on mobile */}
                      <div
                        className={`w-8 h-8 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center ${
                          item.type === "lesson_plan"
                            ? "bg-blue-100 text-blue-600"
                            : "bg-green-100 text-green-600"
                        }`}
                      >
                        <Icon className="w-4 h-4 sm:w-6 sm:h-6" />
                      </div>

                      {/* Content - Reduced right padding on mobile */}
                      <div className="flex-1 min-w-0 pr-12 sm:pr-[96px]">
                        <h3 className="font-semibold text-[#373e46] mb-2 text-sm sm:text-lg">
                          {item.title}
                        </h3>
                        <p className="text-[#373e46] text-xs sm:text-sm mb-3 line-clamp-2">
                          {item.topik_tujuan}
                        </p>

                        {/* Metadata */}
                        <div className="flex flex-wrap gap-2 mb-3">
                          <Badge
                            className={`h-6 px-2 flex items-center rounded-full text-xs font-medium hover:bg-opacity-80 pointer-events-none whitespace-normal ${getItemTypeBadgeColor(item.type)}`}
                          >
                            {getItemTypeLabel(item.type)}
                          </Badge>
                          <Badge className="bg-gray-100 text-gray-800 h-6 px-2 flex items-center rounded-full text-xs font-medium hover:bg-gray-100 hover:text-gray-800 pointer-events-none">
                            {item.mata_pelajaran}
                          </Badge>
                          <Badge className="bg-gray-100 text-gray-800 h-6 px-2 flex items-center rounded-full text-xs font-medium hover:bg-gray-100 hover:text-gray-800 pointer-events-none">
                            {item.jenjang_pendidikan.toUpperCase()}
                          </Badge>
                          {item.type === "assessment" &&
                            "tipe_pertanyaan" in item && (
                              <Badge className="bg-purple-100 text-purple-800 h-6 px-2 flex items-center rounded-full text-xs font-medium hover:bg-purple-100 hover:text-purple-800 pointer-events-none">
                                {item.tipe_pertanyaan}
                              </Badge>
                            )}
                        </div>

                        {/* Date - Made smaller on mobile */}
                        <div className="flex items-center gap-2 text-[10px] sm:text-xs text-gray-500">
                          <CalendarIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span>{formatDate(item.created_at)}</span>
                        </div>
                      </div>

                      {/* Actions - Changed to stack vertically on mobile, horizontally on larger screens */}
                      <div className="absolute top-4 right-4 flex flex-col sm:flex-row gap-2">
                        <Button
                          onClick={() => handleEditItem(item)}
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-2 text-[#373e46] border-gray-300 hover:bg-gray-100 h-10 w-10 p-0"
                        >
                          <EditIcon className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => handleDeleteItem(item)}
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-2 text-red-600 border-red-300 hover:bg-red-50 h-10 w-10 p-0"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Empty State */
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                {filterType === "lesson_plan" ? (
                  <BookOpenIcon className="w-8 h-8 text-gray-400" />
                ) : filterType === "assessment" ? (
                  <ClipboardListIcon className="w-8 h-8 text-gray-400" />
                ) : (
                  <FilterIcon className="w-8 h-8 text-gray-400" />
                )}
              </div>
              <h4 className="text-lg font-semibold text-[#373e46] mb-2">
                {searchQuery
                  ? "Tidak Ada Hasil Pencarian"
                  : "Belum Ada Riwayat"}
              </h4>
              <p className="text-gray-500 mb-4">
                {searchQuery
                  ? `Tidak ditemukan hasil untuk "${searchQuery}"`
                  : filterType === "all"
                    ? "Mulai membuat Lesson Plan atau Assessment untuk melihat riwayat di sini"
                    : `Belum ada ${getItemTypeLabel(filterType).toLowerCase()} yang dibuat`}
              </p>
              {!searchQuery && (
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    onClick={() => navigate("/lesson-plan-builder")}
                    className="bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] hover:opacity-90 text-white px-6 py-2 rounded-xl font-medium transition-all duration-300 hover:shadow-lg"
                  >
                    Buat Lesson Plan
                  </Button>
                  <Button
                    onClick={() => navigate("/assessment-builder")}
                    className="bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] hover:opacity-90 text-white px-6 py-2 rounded-xl font-medium transition-all duration-300 hover:shadow-lg"
                  >
                    Buat Assessment
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Konfirmasi Hapus"
        message={getDeleteDialogMessage()}
        confirmText="Hapus"
        cancelText="Batal"
        variant="danger"
      />
    </div>
  );
};
